// import { AttributeDisplayType as DisplayType } from '../../../admin-frontend/compositions/useCustomerSettings';

import { ChannelAttrsLineWebhook } from './channel';
import { RetailSettings } from './retail';
import { Model } from './typeHelper';
import * as types from '@comsbi/amplify/src/API';

export type Organization = Model<types.GetOrganizationQuery['getOrganization']>;

// @see services/admin-frontend/compositions/useCustomerSettings.ts:18
export const ATTRIBUTE_DISPLAY_TYPE = {
  // common GUI
  SINGLE_TEXT: 'singleText',
  MULTIPLE_TEXT: 'multipleText',
  RADIO: 'radio',
  PULLDOWN: 'pulldown',
  CHECKBOX: 'checkbox',
  NUMBER: 'number',
  DATE: 'date',
  TIME: 'time',
  // unique GUI
  NAME: 'name',
  EMAIL: 'email',
  DATE_OF_BIRTH: 'dateOfBirth',
  ADDRESS: 'address',
  PHONE_NUMBER: 'phoneNumber',
  GENDER: 'gender',
  // Tag
  TAG: 'tag',
  // ConversionCount
  CV: 'cv',
  RANK: 'rank',
} as const;
export type AttributeDisplayType = typeof ATTRIBUTE_DISPLAY_TYPE[keyof typeof ATTRIBUTE_DISPLAY_TYPE];

export type AttributeMasterChoiceItem = {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
};

export type AttributeMaster = {
  key: string;
  label: string;
  displayType: AttributeDisplayType;
  // dataType: typeof AttributeDataType, // displayTypeによって解決されるため不要

  // タグの色、属性の表現固有化などカスタマイズ用途
  options?: {
    // 選択式属性の選択項目
    choices?: AttributeMasterChoiceItem[];
    // 数値属性の単位
    unit?: string;
  };
  personal?: boolean;
  asColumn?: boolean;
  disabled?: boolean;
};

export type TagMaster = AttributeMaster & {
  // 必要ならカスタマイズ可能
};

export type ConversionCountMaster = AttributeMaster & {
  // 必要ならカスタマイズ可能
  options?: AttributeMaster['options'] & {
    shopDisabled?: boolean;
  };
};

type PolicyItem = {
  body?: string;
  linkLabel?: string;
  linkUrl?: string;
};
export type Policy = Omit<types.PolicyInput, 'termsOfUse' | 'privacyPolicy'> & {
  termsOfUse?: PolicyItem;
  privacyPolicy?: PolicyItem;
};

export type OrgLineChatNotification = {
  settings: {
    channelId: string | null;
    email: string;
  }[];
};

type OptionalFunctionItem = {
  valid: boolean;
  createdAt: Date;
  updatedAt: Date;
};

/**
 * オプション機能
 */
export type ValidOptionalFunction = {
  orgReport?: OptionalFunctionItem;
  intakeForm?: OptionalFunctionItem;
  purchaseCoupon?: OptionalFunctionItem;
  hostingAssets?: OptionalFunctionItem;
  integration?: OptionalFunctionItem;
  rank?: OptionalFunctionItem;
  attrsImports?: OptionalFunctionItem;
  addShop?: OptionalFunctionItem;
  customerData?: OptionalFunctionItem;
  //
};

export type CustomerSettings = {
  attributes: AttributeMaster[];
  tags: TagMaster[];
  conversionCounts?: ConversionCountMaster[];
  // CS-6860 LINE情報表示管理
  lineProfile?: {
    personal?: boolean;
    asColumn?: boolean;
  };
  // CS-6860 個人情報完全保護
  personalInformationConcealment?: boolean;
};

type StripeConnection = {
  secretKey: string;
  publicKey: string;
  endpointSecretKey: string;
  qualifiedInvoice?: {
    corporateNumber?: string;
    description?: string;
    footer?: string;
  };
};

type AdminPanelSettings = {
  displayHelpLink: boolean;
};

// CS-8581 SSO対応
//  初期は併用不可で実装を行う
export type AdminAuthSetting = {
  //
  type: 'email' | 'oidc'; // | 'saml';
  // Cognito user poolに設定済みのアイデンティティプロバイダー名
  // @CS-8581:bobby
  //  組み込みとしてLINEログイン, Googleなどの提供も可能。CS-8581現在、非対応
  //  任意文字列でクライアント指定のカスタムidp対応も可能。（別途手動でのCognito上対応が必要）
  identityProviderName?:
    | 'LINE' // COMSBI公式 LINEログインチャネル
    | 'OKTA_CIC' // Okta Customer Identity Cloud
    | string; // その他任意文字列
  // 表示名 ログイン方法名
  displayName?: string;
};

export type OrganizationAttrs = {
  retail?: RetailSettings;
  customer?: CustomerSettings;
  lineChatNotification?: OrgLineChatNotification;
  // CS-7456 オプション機能提供用
  validOptionalFunction?: ValidOptionalFunction;
  //
  stripeConnection: StripeConnection;
  // CS-7983 動的テーマ
  theme?: Record<string, any>;
  // CS-8173 OEM提供
  adminPanelSettings?: AdminPanelSettings;
  // CS-8581 管理画面ログイン設定 設定値がない場合はデフォルトのCognito Eメールログインのみ
  adminAuthSettings?: AdminAuthSetting[];
  // CS-8775 容量拡張
  extend?: { genericId: string }[];
  // CS-8824 組織友達追加設定
  enabledOrgLineWebhooks?: boolean;
  lineWebhooks?: ChannelAttrsLineWebhook[];
  // CS-8994 デモアカウントフラグ
  isDemoAccount?: boolean;
  // CS-8749 シークレットモードフラグ
  isCustomerInfoSecret?: boolean;
  // CS-9135 チャット新UI切り替えフラグ
  useChatV2?: boolean;
  // CS-9212 IP制限
  ipWhitelist?: string[];
};

// CS-8775
export type OrganizationAttrsGenericExtension = {
  attributes: AttributeMaster[];
  tags: TagMaster[];
  conversionCounts?: ConversionCountMaster[];
};
